import { add, multiplyWithFactor, subtract, sum, } from "@pentacode/openapi";
import { DailyResultsDTO } from "../api/rpc";
import { getPayrollIssues, getShiftIssues } from "../issues";
import { TimeEntryType, VacationIncrement } from "../model";
import { getPayrollPeriod } from "../payroll";
import { dateAdd, getContractForDate, lastDayOfPreviousMonth, toDateString, } from "../utils";
import { calcVacationEntitlement } from "../vacation";
import { roundBonusesBalance, roundTimeBalance, roundVacationBalance } from "./balances";
import { createAbsenceHoursGetter, getNominalBonus, getNominalTime, } from "./nominals";
import { fillTimeEntryResults, getDailyAverage, getTotalResults, makeEmptyTimeResult } from "./time-result";
export function getDailyResults(settings, company, employee, entries, { from, to }, previousResults) {
    // !!! This Function also potentially mutates the input TimeEntries and updates their results if not set
    const dailyResults = [];
    let date = from;
    let lastResult = previousResults[previousResults.length - 1];
    // time results are not used in this function, so we can call it before results are updated
    const issues = getShiftIssues(employee, settings, company, entries, { from, to });
    const getTimeBalance = createGetTimeBalance(settings, company, employee);
    const getVacationBalance = createGetVacationBalance(settings, employee);
    const getBonusesBalance = createGetBonusesBalance(settings, employee);
    const allResults = [...previousResults];
    while (date < to) {
        const dayEntries = entries.filter((e) => e.date === date);
        const workEntries = dayEntries.filter((e) => e.type === TimeEntryType.Work);
        const reset = dayEntries.find((e) => e.type === TimeEntryType.ResetLedgers);
        const lastDayOfPreviousMonthDate = lastDayOfPreviousMonth(date);
        const averagesResult = allResults.find((res) => res.date === lastDayOfPreviousMonthDate);
        const contract = getContractForDate(employee.contracts, date);
        let vacationDays = 0;
        let vacationAdjustments = 0;
        if (contract) {
            for (const entry of dayEntries) {
                // THIS MUTATES THE ENTRY
                fillTimeEntryResults(entry, settings, company, employee, workEntries, averagesResult?.averages["13weeks"]);
            }
            // TODO: why does this depend on the contract being set?
            // Also you can have multiple vacation days on a day? That makes no sense
            vacationDays = dayEntries
                .filter((e) => e.type === TimeEntryType.Vacation)
                .reduce((total, e) => add(total, e.days ?? 1), 0);
            // TODO: why does this depend on the contract being set?
            vacationAdjustments = dayEntries
                .filter((e) => e.type === TimeEntryType.VacationAdjustment)
                .reduce((total, e) => add(total, e.days ?? 1), 0);
        }
        const results = accumulateResultsFromEntries(dayEntries, workEntries, Boolean(contract));
        const _13weekAverage = calculate13WeekAverage(allResults, results.work, date, lastResult?.averages["13weeks"]);
        const timeBalance = getTimeBalance(date, lastResult, results, reset, _13weekAverage, false);
        const result = new DailyResultsDTO({
            employeeId: employee.id,
            date,
            averages: {
                "13weeks": _13weekAverage,
            },
            results,
            timeBalance,
            timeBalancePlanned: getTimeBalance(date, lastResult, results, reset, _13weekAverage, true),
            vacationBalance: getVacationBalance(date, lastResult?.vacationBalance.balance, timeBalance, reset, vacationDays, vacationAdjustments),
            bonusesBalance: getBonusesBalance(date, lastResult?.bonusesBalance.balance, results, reset, contract ?? null),
            issues: issues.filter((i) => i.date === date),
        });
        // If this is the last date of the payroll period, check for payroll issues
        if (new Date(dateAdd(date, { days: 1 })).getDate() === settings.startPayrollPeriod) {
            result.issues.push(...getPayrollIssues(settings, company, employee, entries, getPayrollPeriod(date, settings.startPayrollPeriod)));
        }
        dailyResults.push(result);
        allResults.push(result);
        lastResult = result;
        date = dateAdd(date, { days: 1 });
    }
    return dailyResults;
}
/** @deprecated Use `timeBalanceFromDailyResults`, `vacationBalanceFromDailyResults`, `bonusesBalanceFromDailyResults` instead */
export function aggregateDailyResults(results, projected) {
    const firstResult = results[0];
    const today = toDateString(new Date());
    const firstTimeBalance = (projected && firstResult?.date >= today && firstResult?.timeBalancePlanned) || firstResult?.timeBalance;
    const balances = results.reduce((total, result) => {
        const timeBalance = (projected && result.date >= today && result.timeBalancePlanned) || result.timeBalance;
        return {
            time: {
                ...total.time,
                carry: total.time.carry ?? timeBalance.carry,
                actual: add(total.time.actual, timeBalance.actual),
                nominal: add(total.time.nominal, timeBalance.nominal),
                difference: add(total.time.difference, timeBalance.difference),
                work: add(total.time.work, timeBalance.work),
                absences: add(total.time.absences, timeBalance.absences),
                adjustments: add(total.time.adjustments, timeBalance.adjustments),
                balance: timeBalance.balance ?? total.time.balance,
            },
            vacation: {
                ...total.vacation,
                carry: total.vacation.carry ?? result.vacationBalance.carry,
                taken: add(total.vacation.taken, result.vacationBalance.taken),
                adjustments: add(total.vacation.adjustments, result.vacationBalance.adjustments),
                actual: add(total.vacation.actual, result.vacationBalance.actual),
                nominal: add(total.vacation.nominal, result.vacationBalance.nominal),
                difference: add(total.vacation.difference, result.vacationBalance.difference),
                balance: result.vacationBalance.balance ?? total.vacation.balance,
            },
            bonuses: {
                ...total.bonuses,
                carry: total.bonuses.carry ?? result.bonusesBalance.carry,
                taxed: add(total.bonuses.taxed, result.bonusesBalance.taxed),
                untaxed: add(total.bonuses.untaxed, result.bonusesBalance.untaxed),
                actual: add(total.bonuses.actual, result.bonusesBalance.actual),
                nominal: typeof total.bonuses.nominal === "number" && typeof result.bonusesBalance.nominal === "number"
                    ? add(total.bonuses.nominal, result.bonusesBalance.nominal)
                    : (total.bonuses.nominal ?? result.bonusesBalance.nominal),
                difference: typeof total.bonuses.difference === "number" &&
                    typeof result.bonusesBalance.difference === "number"
                    ? add(total.bonuses.difference, result.bonusesBalance.difference)
                    : (total.bonuses.difference ?? result.bonusesBalance.difference),
                balance: result.bonusesBalance.balance ?? total.bonuses.balance,
            },
        };
    }, {
        time: {
            carry: firstTimeBalance?.carry || 0,
            work: 0,
            absences: 0,
            adjustments: 0,
            actual: 0,
            nominal: 0,
            difference: 0,
            balance: firstTimeBalance?.balance || 0,
            reset: firstTimeBalance?.reset,
        },
        vacation: {
            carry: firstResult?.vacationBalance.carry || 0,
            adjustments: 0,
            taken: 0,
            actual: 0,
            nominal: 0,
            difference: 0,
            balance: firstResult?.vacationBalance.balance || 0,
            reset: firstResult?.vacationBalance.reset,
        },
        bonuses: {
            carry: firstResult?.bonusesBalance.carry,
            taxed: 0,
            untaxed: 0,
            actual: 0,
            balance: firstResult?.bonusesBalance.balance,
            reset: firstResult?.bonusesBalance.reset,
        },
    });
    return {
        time: roundTimeBalance(balances.time),
        vacation: roundVacationBalance(balances.vacation),
        bonuses: roundBonusesBalance(balances.bonuses),
    };
}
function accumulateResultsFromEntries(dayEntries, workEntries, hasContractOnDay) {
    if (!hasContractOnDay) {
        return {
            work: makeEmptyTimeResult(),
            workPlanned: makeEmptyTimeResult(),
            absences: makeEmptyTimeResult(),
            adjustments: makeEmptyTimeResult(),
            total: makeEmptyTimeResult(),
        };
    }
    const work = getTotalResults(workEntries.map((e) => e.result).filter(Boolean));
    const workPlanned = getTotalResults(workEntries.map((e) => e.resultPlanned).filter(Boolean));
    const absences = getTotalResults(dayEntries
        .filter((e) => [
        TimeEntryType.Vacation,
        TimeEntryType.Sick,
        TimeEntryType.SickInKUG,
        TimeEntryType.ChildSick,
        TimeEntryType.CompDay,
    ].includes(e.type))
        .map((e) => e.result)
        .filter(Boolean));
    const adjustments = getTotalResults(dayEntries
        .filter((e) => [TimeEntryType.HourAdjustment].includes(e.type))
        .map((e) => e.result)
        .filter(Boolean));
    const total = getTotalResults([work, absences, adjustments]);
    return {
        work,
        workPlanned,
        absences,
        adjustments,
        total,
    };
}
export const createGetTimeBalance = (settings, company, employee) => {
    function getTimeBalance(date, lastResult, // last result can be undefined if there are no previous results
    results, reset, _13weekAverage, planned) {
        const carry = (planned ? lastResult?.timeBalancePlanned?.balance : lastResult?.timeBalance.balance) || 0;
        const nominal = getNominalTime(settings, company, employee, { from: date, to: dateAdd(date, { days: 1 }) }, // TODO: getNextDay
        //we go day by day here, so we can use the last 13 weeks average
        createAbsenceHoursGetter([{ date, averages: { "13weeks": _13weekAverage } }]));
        const work = planned ? results.workPlanned?.base.duration || 0 : results.work.base.duration;
        const absences = results.absences.base.duration;
        const adjustments = results.adjustments.base.duration;
        const actual = sum(work, absences, adjustments);
        const difference = subtract(actual, nominal);
        const base = reset?.hours ?? carry;
        const balance = add(base, difference);
        return {
            reset: reset?.hours ?? undefined,
            nominal,
            work,
            adjustments,
            absences,
            actual,
            difference,
            carry,
            balance,
        };
    }
    return getTimeBalance;
};
export const createGetVacationBalance = (settings, employee) => {
    function getVacationBalance(date, lastVacationBalance, timeBalance, reset, vacationDays, vacationAdjustments) {
        const contract = getContractForDate(employee.contracts, date);
        const carry = lastVacationBalance || 0;
        const base = reset?.days ?? carry;
        const vacationFactor = (contract?.vacationIncrement === VacationIncrement.Hourly && timeBalance.nominal
            ? timeBalance.actual / timeBalance.nominal
            : 1);
        let nominal = calcVacationEntitlement(employee.contracts, { from: date, to: dateAdd(date, { days: 1 }) }, settings);
        nominal = multiplyWithFactor(nominal, vacationFactor);
        const actual = add(vacationDays, vacationAdjustments);
        const difference = subtract(nominal, actual);
        const balance = add(base, difference);
        return {
            reset: reset?.days ?? undefined,
            nominal,
            taken: vacationDays,
            adjustments: vacationAdjustments,
            actual,
            difference,
            carry,
            balance,
        };
    }
    return getVacationBalance;
};
export const createGetBonusesBalance = (settings, employee) => {
    function getBonusesBalance(date, lastBonusesBalance, results, reset, contract) {
        const taxed = results.total.bonuses
            .filter((b) => !b.taxFree)
            .reduce((total, b) => add(total, b.wages), 0);
        const untaxed = results.total.bonuses
            .filter((b) => b.taxFree)
            .reduce((total, b) => add(total, b.wages), 0);
        const actual = settings.includeTaxedBonusesInBalance ? add(taxed, untaxed) : untaxed;
        if (!contract?.enableSFNLedger) {
            return {
                taxed,
                untaxed,
                actual,
            };
        }
        const nominal = getNominalBonus(employee.contracts, { from: date, to: dateAdd(date, { days: 1 }) });
        const difference = subtract(actual, nominal);
        // Bonuses balance is always reset with the beginning of a new year
        const carry = date.endsWith("01-01") ? 0 : lastBonusesBalance || 0;
        const base = reset?.resetBonus ?? carry;
        const balance = add(base, difference);
        return {
            reset: reset?.resetBonus ?? undefined,
            nominal,
            taxed,
            untaxed,
            actual,
            difference,
            carry,
            balance,
        };
    }
    return getBonusesBalance;
};
/**
 * Searches for the latest day that can be classified as work.
 *
 * @param date The reference date, used as a fallback or when `workResultsOnDate` also includes work.
 * @param workResultsOnDate The accumulated results for all time entries of type work on `date`.
 * @param allResults The daily results in which we can search for a work day. Have to be sorted.
 *
 *  Note: Only exported for tests - preconditions are complex, don't use unless you know what you're doing.
 *
 * @returns The date of the last days classified as work.
 */
export function findLastWorkday(date, workResultsOnDate, allResults) {
    if (workResultsOnDate.base.duration) {
        return date;
    }
    /* Go back daily until we find results with defined `work.days`.
     * This is inconsistent with above, where duration is the deciding property,
     * but probably needed because of different types - TimeResult do not have a `days` property.
     */
    for (let i = allResults.length - 1; i >= 0; i--) {
        const candidate = allResults[i];
        if (candidate.results.work.days) {
            /* This keeps the behavior before the refactor.
             * We might want to check if the date is set, and only then return the candidates date,
             * not immediately fall back to `date` once we find an entry with `days` defined.
             */
            return candidate.date || date;
        }
    }
    return date;
}
/**
 * Calculates the 13-week averages for the first of the month.
 * For all other days it carries the last calculated average.
 *
 *  Note: Only exported for tests - preconditions are complex, don't use unless you know what you're doing.
 *
 * @param allResults Already calculated daily results. Have to be sorted.
 * @param workResultsOnDay The accumulated results for all time entries of type work on `date`.
 * @param date The `date` the averages are calculated for.
 * @param lastAverages The last result that was calculated, used as a fallback.
 *
 * @returns The calculated averages based on the given results.
 */
export function calculate13WeekAverage(allResults, workResultsOnDay, date, lastAverages) {
    // For historical reasons, we calculate a new 13 week average for each month
    // and use the same average for the whole month instead of calculating a new
    // (more accurate) value for each day
    if (!dateAdd(date, { days: 1 }).endsWith("-01")) {
        return lastAverages || makeEmptyTimeResult();
    }
    // We start calculating from the last work day
    const lastWorkDate = findLastWorkday(date, workResultsOnDay, allResults);
    const to = dateAdd(lastWorkDate, { days: 1 });
    // Averages are calculated from the last 13 weeks
    // (calculating backwards from the last work day)
    const from = dateAdd(to, { weeks: -13 });
    const averagesInput = allResults.filter((r) => r.date >= from && r.date < to);
    // Only work results are considered
    const workResults = averagesInput.map((e) => e.results.work);
    // This assumes that `days` and `base.duration` are either both 0, or both not 0
    // If this is not true, the results for averages will be off
    workResults.push(workResultsOnDay);
    return getDailyAverage(workResults);
}
//# sourceMappingURL=daily-results.js.map